/**
 * 来場者スタンプ獲得演出サービス
 * 画面遷移を跨いでも新規押印演出を出せるよう、一時情報を保存します。
 */

import webStorage from '../../../shared/services/webStorage';
import { AppError } from '../../../shared/utils/errorHandling';
import { logWarn } from '../../../shared/utils/logger';
import { validateRequiredString } from '../../../shared/utils/validation';

/** 一時保存キー接頭辞 */
const STAMP_CELEBRATION_STORAGE_KEY_PREFIX = '@ikomasai/stamp_celebration/';

/** 保存失敗エラーコード */
export const VISITOR_STAMP_CELEBRATION_SAVE_FAILED_CODE = 'VISITOR_STAMP_CELEBRATION_SAVE_FAILED';

/** 読み込み失敗エラーコード */
export const VISITOR_STAMP_CELEBRATION_LOAD_FAILED_CODE = 'VISITOR_STAMP_CELEBRATION_LOAD_FAILED';

/** sessionToken から保存キーを作る */
const toStorageKey = (sessionToken) => `${STAMP_CELEBRATION_STORAGE_KEY_PREFIX}${sessionToken}`;

/**
 * 一時保存データを正規化する
 * @param {any} celebrations - 一時保存データ
 * @returns {Array<{ eventId: string, claimedAt: string }>} 正規化済みデータ
 */
const normalizeCelebrations = (celebrations) => {
  if (!Array.isArray(celebrations)) {
    return [];
  }

  return celebrations
    .map((celebration) => ({
      eventId: typeof celebration?.eventId === 'string' ? celebration.eventId : '',
      claimedAt: typeof celebration?.claimedAt === 'string' ? celebration.claimedAt : '',
    }))
    .filter((celebration) => Boolean(celebration.eventId) && Boolean(celebration.claimedAt));
};

/**
 * 保存済み一時データを読み込む
 * @param {string} sessionToken - セッショントークン
 * @returns {Promise<Array<{ eventId: string, claimedAt: string }>>} 一時データ
 */
export const loadPendingStampCelebrations = async (sessionToken) => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'sessionToken');

  if (!sessionTokenValidation.isValid) {
    return [];
  }

  try {
    const serialized = await webStorage.getItem(toStorageKey(sessionToken.trim()));

    if (!serialized) {
      return [];
    }

    try {
      return normalizeCelebrations(JSON.parse(serialized));
    } catch (error) {
      logWarn('スタンプ獲得演出データが破損していたため削除します。', {
        message: error instanceof Error ? error.message : String(error),
      });
      await webStorage.removeItem(toStorageKey(sessionToken.trim()));
      return [];
    }
  } catch (error) {
    throw new AppError('スタンプ獲得演出データの読み込みに失敗しました。', {
      code: VISITOR_STAMP_CELEBRATION_LOAD_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error instanceof Error ? error.message : String(error),
      },
      cause: error instanceof Error ? error : undefined,
    });
  }
};

/**
 * 新規押印演出の一時データを削除する
 * @param {string} sessionToken - セッショントークン
 * @returns {Promise<void>}
 */
export const clearPendingStampCelebrations = async (sessionToken) => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'sessionToken');

  if (!sessionTokenValidation.isValid) {
    return;
  }

  try {
    await webStorage.removeItem(toStorageKey(sessionToken.trim()));
  } catch (error) {
    throw new AppError('スタンプ獲得演出データの読み込みに失敗しました。', {
      code: VISITOR_STAMP_CELEBRATION_LOAD_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error instanceof Error ? error.message : String(error),
      },
      cause: error instanceof Error ? error : undefined,
    });
  }
};

/**
 * 新規押印演出の一時データを追加する
 * @param {string} sessionToken - セッショントークン
 * @param {string} eventId - イベントID
 * @param {string} claimedAt - 押印日時
 * @returns {Promise<void>}
 */
export const queuePendingStampCelebration = async (sessionToken, eventId, claimedAt) => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'sessionToken');
  const eventIdValidation = validateRequiredString(eventId, 'eventId');
  const claimedAtValidation = validateRequiredString(claimedAt, 'claimedAt');

  if (!sessionTokenValidation.isValid || !eventIdValidation.isValid || !claimedAtValidation.isValid) {
    return;
  }

  /** 現在の一時データ */
  const currentCelebrations = await loadPendingStampCelebrations(sessionToken.trim());

  /** 次回表示用一時データ */
  const nextCelebrations = [...currentCelebrations];

  /** 既存インデックス */
  const existingIndex = nextCelebrations.findIndex((celebration) => celebration.eventId === eventId.trim());

  if (existingIndex >= 0) {
    nextCelebrations[existingIndex] = { eventId: eventId.trim(), claimedAt: claimedAt.trim() };
  } else {
    nextCelebrations.push({ eventId: eventId.trim(), claimedAt: claimedAt.trim() });
  }

  try {
    await webStorage.setItem(
      toStorageKey(sessionToken.trim()),
      JSON.stringify(normalizeCelebrations(nextCelebrations)),
    );
  } catch (error) {
    throw new AppError('スタンプ獲得演出データの保存に失敗しました。', {
      code: VISITOR_STAMP_CELEBRATION_SAVE_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error instanceof Error ? error.message : String(error),
      },
      cause: error instanceof Error ? error : undefined,
    });
  }
};

/**
 * 新規押印演出の一時データを読み込んで削除する
 * @param {string} sessionToken - セッショントークン
 * @returns {Promise<Array<{ eventId: string, claimedAt: string }>>} 一時データ
 */
export const consumePendingStampCelebrations = async (sessionToken) => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'sessionToken');

  if (!sessionTokenValidation.isValid) {
    return [];
  }

  /** 保存済み一時データ */
  const storedCelebrations = await loadPendingStampCelebrations(sessionToken.trim());
  await clearPendingStampCelebrations(sessionToken.trim());

  return storedCelebrations;
};
